import { useState } from 'react';
import { AdminInvoiceDocument } from '../../services/invoicesApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ResendInvoiceDialogProps {
  doc: AdminInvoiceDocument;
  /**
   * #823 — the stored address is permanently undeliverable (a Permanent bounce, or the provider
   * has suppressed it). Resending to it cannot work, so a different address is required rather
   * than optional.
   */
  requireOverride?: boolean;
  onConfirm: (emailOverride: string | undefined) => Promise<void>;
  onClose: () => void;
}

/**
 * Confirm dialog for "Resend invoice" (AC-10). The optional email override
 * applies to this send only — it never mutates the customer's profile email.
 */
export default function ResendInvoiceDialog({
  doc,
  requireOverride = false,
  onConfirm,
  onClose,
}: ResendInvoiceDialogProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setError(null);
    const trimmed = email.trim();
    if (requireOverride && !trimmed) {
      setError('This address is undeliverable — enter a different one');
      return;
    }
    if (trimmed && !EMAIL_RE.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }
    if (requireOverride && trimmed.toLowerCase() === (doc.sentTo ?? '').toLowerCase()) {
      setError('That is the address that failed — enter a different one');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(trimmed || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resend failed');
      setSubmitting(false);
    }
  };

  const label = doc.docType === 'CREDIT_NOTE' ? 'credit note' : 'invoice';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{requireOverride ? `Send ${label} to another address` : `Resend ${label}`}</h3>
          <button className="admin-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="admin-modal-body">
          <p style={{ fontSize: '0.875rem', marginTop: 0 }}>
            Resend {label} <strong>{doc.invoiceNumber}</strong>
            {doc.sentTo ? <> — last sent to <strong>{doc.sentTo}</strong></> : null}.
            The same stored PDF will be re-sent.
          </p>
          {requireOverride && (
            <div className="admin-info-banner danger" style={{ marginBottom: '0.75rem' }}>
              <strong>{doc.sentTo ?? 'That address'}</strong> rejected this permanently, and the
              provider will refuse to try it again. Enter a different address.
            </div>
          )}
          <div className="admin-form-group">
            <label className="admin-form-label">
              {requireOverride ? 'Send to a different email *' : 'Send to a different email (optional)'}
            </label>
            <input
              type="email"
              className="admin-form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={requireOverride
                ? 'A different, working email address'
                : "Leave blank to use the customer's email"}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', margin: '0.375rem 0 0' }}>
              Applies to this send only — the customer's profile email is not changed.
            </p>
          </div>
          {error && <div className="admin-info-banner danger">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn admin-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="admin-btn admin-btn-primary" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Sending...' : requireOverride ? 'Send to this address' : 'Confirm Resend'}
          </button>
        </div>
      </div>
    </div>
  );
}
